import * as React from "react";
import { useMemo, useEffect } from "react";
import styled from "styled-components";
import useTableLayout from "components/common/EntityDataTable/hooks/useTableLayout";
import useUpdateUserLayoutPreferences from "components/common/EntityDataTable/hooks/useUpdateUserLayoutPreferences";
import { useTableEventHandlers } from "components/common/EntityDataTable";
import { Spinner, PaginatedList, SearchForm, NoSearchResult, EntityDataTable } from "components/common";
import EntityFilters from "components/common/EntityFilters";
import TableFetchContextProvider from "components/common/PaginatedEntityTable/TableFetchContextProvider";
import useFetchEntities from "components/common/PaginatedEntityTable/useFetchEntities";
import useOnRefresh from "components/common/PaginatedEntityTable/useOnRefresh";
import { useWithLocalState, useWithURLParams } from "./useFiltersAndPagination";
const SearchRow = styled.div`
  margin-bottom: 5px;
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;
const INITIAL_DATA = {
  pagination: { total: 0 },
  list: [],
  attributes: [],
  meta: null
};
const PaginatedEntityTableInner = ({
  additionalAttributes = [],
  bulkSelection = void 0,
  columnRenderers,
  entityActions,
  entityAttributesAreCamelCase,
  expandedSectionRenderers = void 0,
  fetchEntities,
  fetchOptions,
  filterValueRenderers = void 0,
  focusSearchAfterMount = false,
  humanName,
  isLoadingLayoutPreferences,
  keyFn,
  layoutConfig,
  middleSection: MiddleSection = void 0,
  externalSearch = void 0,
  onChangeFilters,
  onDataLoaded = void 0,
  paginationState,
  queryHelpComponent = void 0,
  reactQueryOptions,
  searchPlaceholder = void 0,
  setQuery,
  tableLayout,
  topRightCol = void 0,
  withoutURLParams = false
}) => {
  const { mutateAsync: updateTableLayout } = useUpdateUserLayoutPreferences(tableLayout.entityTableId);
  const fetchKey = useMemo(() => keyFn(fetchOptions), [fetchOptions, keyFn]);
  const {
    data: paginatedEntities = INITIAL_DATA,
    isInitialLoading: isLoadingEntities,
    refetch
  } = useFetchEntities({
    fetchKey,
    searchParams: fetchOptions,
    enabled: !isLoadingLayoutPreferences,
    fetchEntities,
    humanName,
    fetchOptions: reactQueryOptions
  });
  useOnRefresh(refetch);
  useEffect(() => {
    if (!onDataLoaded || isLoadingEntities) {
      return;
    }
    onDataLoaded(paginatedEntities);
  }, [isLoadingEntities, onDataLoaded, paginatedEntities]);
  const appSection = `${tableLayout.entityTableId}-list`;
  const {
    onLayoutPreferencesChange,
    onPageSizeChange,
    onResetLayoutPreferences,
    onSearch,
    onSearchReset,
    onSortChange
  } = useTableEventHandlers({
    appSection,
    paginationQueryParameter: paginationState,
    updateTableLayout,
    setQuery
  });
  const columnSchemas = useMemo(
    () => {
      var _a;
      return [
        ...(_a = paginatedEntities == null ? void 0 : paginatedEntities.attributes) != null ? _a : [],
        ...additionalAttributes.map((attribute) => ({ isDerived: true, ...attribute }))
      ];
    },
    [additionalAttributes, paginatedEntities == null ? void 0 : paginatedEntities.attributes]
  );
  if (isLoadingLayoutPreferences || isLoadingEntities) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  const {
    list,
    meta,
    pagination: { total },
    attributes
  } = paginatedEntities;
  return /* @__PURE__ */ React.createElement(
    TableFetchContextProvider,
    {
      refetch,
      searchParams: fetchOptions,
      attributes,
      entityTableId: tableLayout.entityTableId
    },
    /* @__PURE__ */ React.createElement(
      PaginatedList,
      {
        pageSize: layoutConfig.pageSize,
        showPageSizeSelect: false,
        totalItems: total,
        useQueryParameter: !withoutURLParams,
        onChange: withoutURLParams ? (currentPage, pageSize) => paginationState.setPagination({ page: currentPage, pageSize }) : void 0
      },
      !externalSearch && /* @__PURE__ */ React.createElement(SearchRow, null, /* @__PURE__ */ React.createElement(
        SearchForm,
        {
          focusAfterMount: focusSearchAfterMount,
          onSearch,
          onReset: onSearchReset,
          query: fetchOptions.query,
          placeholder: searchPlaceholder != null ? searchPlaceholder : `Search for ${humanName}`,
          queryHelpComponent
        },
        /* @__PURE__ */ React.createElement("div", { style: { marginBottom: 5 } }, /* @__PURE__ */ React.createElement(
          EntityFilters,
          {
            attributes,
            urlQueryFilters: fetchOptions.filters,
            setUrlQueryFilters: onChangeFilters,
            filterValueRenderers,
            appSection
          }
        ))
      ), topRightCol),
      MiddleSection ? /* @__PURE__ */ React.createElement(MiddleSection, { searchParams: fetchOptions, setFilters: onChangeFilters }) : null,
      (list == null ? void 0 : list.length) === 0 ? /* @__PURE__ */ React.createElement(NoSearchResult, null, "No ", humanName, " have been found.") : /* @__PURE__ */ React.createElement(
        EntityDataTable,
        {
          entities: list,
          defaultDisplayedColumns: tableLayout.defaultDisplayedAttributes,
          layoutPreferences: {
            attributes: layoutConfig.attributes,
            order: layoutConfig.order
          },
          defaultColumnOrder: tableLayout.defaultColumnOrder,
          onResetLayoutPreferences,
          onLayoutPreferencesChange,
          expandedSectionRenderers,
          bulkSelection,
          onSortChange,
          onPageSizeChange,
          pageSize: layoutConfig.pageSize,
          activeSort: layoutConfig.sort,
          entityActions,
          columnRenderers,
          columnSchemas,
          entityAttributesAreCamelCase,
          meta
        }
      )
    )
  );
};
const TableWithLocalState = ({ ...props }) => {
  const { fetchOptions, setQuery, onChangeFilters, paginationState } = useWithLocalState(props.layoutConfig);
  const effectiveFetchOptions = props.externalSearch ? { ...fetchOptions, query: props.externalSearch.query } : fetchOptions;
  return /* @__PURE__ */ React.createElement(
    PaginatedEntityTableInner,
    {
      ...props,
      fetchOptions: effectiveFetchOptions,
      setQuery: props.externalSearch ? () => {
      } : setQuery,
      onChangeFilters,
      paginationState,
      onDataLoaded: props.onDataLoaded
    }
  );
};
const TableWithURLParams = ({ ...props }) => {
  const { fetchOptions, setQuery, onChangeFilters, paginationState } = useWithURLParams(props.layoutConfig);
  const effectiveFetchOptions = props.externalSearch ? { ...fetchOptions, query: props.externalSearch.query } : fetchOptions;
  return /* @__PURE__ */ React.createElement(
    PaginatedEntityTableInner,
    {
      ...props,
      fetchOptions: effectiveFetchOptions,
      setQuery: props.externalSearch ? () => {
      } : setQuery,
      onChangeFilters,
      paginationState,
      onDataLoaded: props.onDataLoaded
    }
  );
};
const PaginatedEntityTable = ({
  fetchOptions: reactQueryOptions = void 0,
  ...props
}) => {
  const { layoutConfig, isInitialLoading: isLoadingLayoutPreferences } = useTableLayout(props.tableLayout);
  const Wrapper = props.withoutURLParams ? TableWithLocalState : TableWithURLParams;
  return /* @__PURE__ */ React.createElement(
    Wrapper,
    {
      ...props,
      layoutConfig,
      isLoadingLayoutPreferences,
      reactQueryOptions,
      onDataLoaded: props.onDataLoaded
    }
  );
};
export default PaginatedEntityTable;
